import React from "react";
import PropTypes from "prop-types";
import { Query } from "react-apollo";
import * as R from "ramda";
import { css } from "glamor";
import moment from "moment";
import { Row, Col, Form, Input, DatePicker } from "antd";
import Heading from "@arachnys/styleguide/Heading";
import Button from "@arachnys/styleguide/Button";
import { Fieldset } from "@arachnys/styleguide/Fieldset";
import SchemaSelect from "@arachnys/styleguide/SchemaSelect";
import { countryOptions } from "@arachnys/flow-app/src/data";
import { REQUEST_FORM_SCHEMAS } from "@arachnys/flow-app/src/page/RequestPortal/queries";
import SearchField from "@arachnys/flow-app/src/page/RequestPortal/SearchField";

import {
    getJsonSchemaFromPath,
    withApolloState,
} from "@arachnys/flow-app/src/util";
import WithAuth from "@arachnys/flow-app/src/container/WithAuth";
import EntityRelations from "./EntityRelations";
import RequestDetails from "./ResearchRequestFormDetails";

const styles = {
    requestButton: css({
        float: "right",
        marginRight: "28px",
    }),
    form: css({
        "& .ant-form-item-label": {
            textAlign: "left",
        },
    }),
};

class NameField extends React.Component {
    redirectToRenew = ulid =>
        this.props.history.push(`/tickets/${ulid}/renew/`);

    render() {
        const { isNewRequest, ...otherProps } = this.props;

        return isNewRequest ? (
            <Input
                {...otherProps}
                placeholder="Entity Name"
                disabled={this.props.disabled.entity_name}
            />
        ) : (
            <SearchField
                {...otherProps}
                placeholder="Entity Name"
                disabled={this.props.disabled.entity_name}
                handleSelect={this.redirectToRenew}
                refreshData={value =>
                    this.props.form.setFieldsValue({ entity_name: value })
                }
            />
        );
    }
}

// TODO: USE SCHEMA INSTEAD!!!
const FIELDS = [
    "entity_name",
    "entity_type",
    "local_language_name",
    "date_of_birth",
    "id_number",
    "physical_address_street_address",
    "physical_address_city",
    "physical_address_state",
    "physical_address_postal_code",
    "physical_address_country_code",
    "legal_address_street_address",
    "legal_address_city",
    "legal_address_state",
    "legal_address_postal_code",
    "legal_address_country_code",
    "phone_number",
    "email",
    "website",
    "request_type",
    "business_unit",
    "billing_code",
    "service_line",
    "client_type",
    "comments",
    "relations",
    "monitoring_type",
];

const getDisabledFieldsSetting = disable => {
    return R.is(Object, disable)
        ? disable
        : R.pipe(
              R.map(fieldName => [fieldName, !!disable]),
              R.fromPairs
          )(FIELDS);
};

const BasicInfo = ({
    getFieldDecorator,
    disabled,
    entitySchema,
    form,
    researchRequest,
    history,
}) => {
    const typeInitialValue = R.path(["entity", "entityType"], researchRequest);

    const entityTypeSchema = getJsonSchemaFromPath("entityType", entitySchema);

    return (
        <>
            <Form.Item label="Entity name">
                {getFieldDecorator("entity_name", {
                    rules: [
                        {
                            required: true,
                            message: "Please input the entity name",
                        },
                    ],
                    initialValue: R.path(["entity", "name"], researchRequest),
                })(
                    <NameField
                        history={history}
                        disabled={disabled}
                        form={form}
                        isNewRequest={typeInitialValue}
                    />
                )}
            </Form.Item>
            <Form.Item label="Entity Type">
                {getFieldDecorator("entity_type", {
                    initialValue: typeInitialValue,
                    rules: [
                        {
                            required: true,
                            message: "Please select an entity type",
                        },
                    ],
                })(
                    <SchemaSelect
                        placeholder="Entity Type"
                        disabled={disabled.entity_type}
                        schema={entityTypeSchema}
                    />
                )}
            </Form.Item>
            <Form.Item label="Local Language Name">
                {getFieldDecorator("local_language_name", {
                    initialValue: R.path(
                        ["entity", "alternativeNames", "localNames", 0],
                        researchRequest
                    ),
                })(
                    <Input
                        placeholder="Local Language Name"
                        disabled={disabled.local_language_name}
                    />
                )}
            </Form.Item>
            <Form.Item label="Date of Incorporation/Birth">
                {getFieldDecorator("date_of_birth", {
                    initialValue: R.pipe(
                        R.path(["entity", "dateOfBirth"]),
                        R.unless(R.either(R.isNil, R.isEmpty), moment)
                    )(researchRequest),
                })(
                    <DatePicker
                        placeholder="Select date of birth"
                        disabled={disabled.date_of_birth}
                    />
                )}
            </Form.Item>
            <Form.Item label="ID Number">
                {getFieldDecorator("id_number", {
                    initialValue: R.path(
                        ["entity", "additionalProperties", "idNumber"],
                        researchRequest
                    ),
                })(
                    <Input
                        placeholder="ID Number"
                        disabled={disabled.id_number}
                    />
                )}
            </Form.Item>
        </>
    );
};

const PhysicalAddress = ({
    getFieldDecorator,
    disabled,
    entitySchema,
    researchRequest,
    countryRequired = false,
}) => {
    const countrySchema = {
        ...getJsonSchemaFromPath(`physicalAddress.countryCode`, entitySchema),
        options: countryOptions,
    };

    const address = R.pathOr(
        {},
        ["entity", "physicalAddress"],
        researchRequest
    );

    return (
        <>
            <Form.Item label="Street Address">
                {getFieldDecorator("physical_address_street_address", {
                    initialValue: address.streetAddress,
                })(
                    <Input
                        placeholder="Street Address"
                        disabled={disabled.physical_address_street_address}
                    />
                )}
            </Form.Item>
            <Form.Item label="City">
                {getFieldDecorator("physical_address_city", {
                    initialValue: address.city,
                })(
                    <Input
                        placeholder="City"
                        disabled={disabled.physical_address_city}
                    />
                )}
            </Form.Item>
            <Form.Item label="State">
                {getFieldDecorator("physical_address_state", {
                    initialValue: address.state,
                })(
                    <Input
                        placeholder="State"
                        disabled={disabled.physical_address_state}
                    />
                )}
            </Form.Item>
            <Form.Item label="Postal Code">
                {getFieldDecorator("physical_address_postal_code", {
                    initialValue: address.postalCode,
                })(
                    <Input
                        placeholder="Postal Code"
                        disabled={disabled.physical_address_postal_code}
                    />
                )}
            </Form.Item>
            <Form.Item label="Country">
                {getFieldDecorator("physical_address_country_code", {
                    initialValue: address.countryCode,
                    rules: [
                        {
                            required: countryRequired,
                            message: "Please choose country from the list",
                        },
                    ],
                })(
                    <SchemaSelect
                        placeholder="Country"
                        showSearch
                        disabled={disabled.physical_address_country_code}
                        schema={countrySchema}
                    />
                )}
            </Form.Item>
        </>
    );
};

const ContactInfo = ({
    getFieldDecorator,
    disabled,
    researchRequest: { entity: { additionalProperties: props = {} } = {} },
}) => (
    <>
        <Form.Item label="Phone number">
            {getFieldDecorator("phone_number", {
                initialValue: props.phoneNumber,
            })(
                <Input
                    placeholder="Phone Number"
                    disabled={disabled.phone_number}
                />
            )}
        </Form.Item>
        <Form.Item label="Email">
            {getFieldDecorator("email", {
                initialValue: props.email,
            })(<Input placeholder="Email" disabled={disabled.email} />)}
        </Form.Item>
        <Form.Item label="Website">
            {getFieldDecorator("website", {
                initialValue: props.website,
            })(<Input placeholder="Website" disabled={disabled.website} />)}
        </Form.Item>
    </>
);

const LegalAddress = ({
    getFieldDecorator,
    disabled,
    entitySchema,
    researchRequest,
    countryRequired = false,
}) => {
    const countrySchema = {
        ...getJsonSchemaFromPath(`legalAddress.countryCode`, entitySchema),
        options: countryOptions,
    };

    const address = R.pathOr({}, ["entity", "legalAddress"], researchRequest);

    return (
        <>
            <Form.Item label="Street Address">
                {getFieldDecorator("legal_address_street_address", {
                    initialValue: address.streetAddress,
                })(
                    <Input
                        placeholder="Street Address"
                        disabled={disabled.legal_address_street_address}
                    />
                )}
            </Form.Item>
            <Form.Item label="City">
                {getFieldDecorator("legal_address_city", {
                    initialValue: address.city,
                })(
                    <Input
                        placeholder="City"
                        disabled={disabled.legal_address_city}
                    />
                )}
            </Form.Item>
            <Form.Item label="State">
                {getFieldDecorator("legal_address_state", {
                    initialValue: address.state,
                })(
                    <Input
                        placeholder="State"
                        disabled={disabled.legal_address_state}
                    />
                )}
            </Form.Item>
            <Form.Item label="Postal Code">
                {getFieldDecorator("legal_address_postal_code", {
                    initialValue: address.postalCode,
                })(
                    <Input
                        placeholder="Postal Code"
                        disabled={disabled.legal_address_postal_code}
                    />
                )}
            </Form.Item>
            <Form.Item label="Country">
                {getFieldDecorator("legal_address_country_code", {
                    initialValue: address.countryCode,
                    rules: [
                        {
                            required: countryRequired,
                            message: "Please choose country from the list",
                        },
                    ],
                })(
                    <SchemaSelect
                        placeholder="Country"
                        showSearch
                        disabled={disabled.legal_address_country_code}
                        schema={countrySchema}
                    />
                )}
            </Form.Item>
        </>
    );
};

const AdditionalInformation = ({
    getFieldDecorator,
    disabled,
    researchRequest,
}) => (
    <Fieldset>
        <Row gutter={24}>
            <Col span={24}>
                <Form.Item label="Additional Information">
                    {getFieldDecorator("comments", {
                        initialValue: researchRequest.comments,
                    })(
                        <Input.TextArea
                            rows={4}
                            placeholder="Additional Information"
                            disabled={disabled.comments}
                        />
                    )}
                </Form.Item>
            </Col>
        </Row>
    </Fieldset>
);

class ResearchRequestForm extends React.Component {
    render() {
        const {
            onSubmit,
            form,
            researchRequest = {},
            entitySchema,
            researchRequestSchema,
            relationSchema,
            customer: { customerType },
            disable,
            ...props
        } = this.props;

        const { getFieldDecorator } = form;

        const disabled = getDisabledFieldsSetting(disable);

        const essentials = { researchRequest, disabled, getFieldDecorator };
        const schemas = {
            entitySchema,
            researchRequestSchema,
            relationSchema,
        };

        const shouldShowRelations = !(
            disabled.relations && R.isEmpty(researchRequest.relations)
        );

        const entityTypeFromForm = form.getFieldValue("entity_type");
        const entityTypeFromResearchRequest = R.path(
            ["entity", "entityType"],
            researchRequest
        );
        const isIndividual =
            entityTypeFromResearchRequest === "INDIVIDUAL" ||
            entityTypeFromForm === "INDIVIDUAL";

        return (
            <Form
                onSubmit={onSubmit}
                layout="horizontal"
                className={`${styles.form}`}
            >
                <Heading level={1}>Add primary entity</Heading>
                <Fieldset>
                    <Row gutter={24}>
                        <Col span={12}>
                            <b>Basic Information</b>
                            <BasicInfo
                                {...essentials}
                                {...schemas}
                                history={props.history}
                                form={form}
                            />
                        </Col>
                        <Col span={12}>
                            {isIndividual ? (
                                <>
                                    <b>Physical Address</b>
                                    <PhysicalAddress
                                        {...essentials}
                                        {...schemas}
                                        countryRequired
                                    />
                                </>
                            ) : (
                                <>
                                    <b>Legal Address</b>
                                    <LegalAddress
                                        {...essentials}
                                        {...schemas}
                                        countryRequired
                                    />
                                </>
                            )}
                        </Col>
                    </Row>
                </Fieldset>
                <Fieldset>
                    <Row gutter={24}>
                        <Col span={12}>
                            <b>Contact Info</b>
                            <ContactInfo {...essentials} />
                        </Col>
                        <Col span={12}>
                            {isIndividual ? (
                                <>
                                    <b>Legal Address</b>
                                    <LegalAddress
                                        {...essentials}
                                        {...schemas}
                                    />
                                </>
                            ) : (
                                <>
                                    <b>Physical Address</b>
                                    <PhysicalAddress
                                        {...essentials}
                                        {...schemas}
                                    />
                                </>
                            )}
                        </Col>
                    </Row>
                </Fieldset>

                {shouldShowRelations && (
                    <>
                        <Heading level={1}>Relations</Heading>
                        <EntityRelations
                            {...essentials}
                            form={form}
                            schemas={schemas}
                        />
                    </>
                )}
                <Heading level={1}>Request Details</Heading>
                <RequestDetails
                    customerType={customerType}
                    {...essentials}
                    {...schemas}
                />
                <AdditionalInformation {...essentials} />
                {disable !== true && (
                    <Button.Primary htmlType="submit" {...styles.requestButton}>
                        Save and Continue
                    </Button.Primary>
                )}
            </Form>
        );
    }
}

export const WrappedResearchRequestForm = Form.create()(ResearchRequestForm);

WrappedResearchRequestForm.propTypes = {
    onSubmit: PropTypes.func,
    disable: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),
    form: PropTypes.object,
    entitySchema: PropTypes.object.isRequired,
    relationSchema: PropTypes.object.isRequired,
    researchRequestSchema: PropTypes.object.isRequired,
    customer: PropTypes.shape({
        customerType: PropTypes.oneOf(["INTERNAL", "EXTERNAL"]),
    }).isRequired,
};

const ResearchRequestFormQuery = props => (
    <Query
        query={REQUEST_FORM_SCHEMAS}
        fetchPolicy="cache-and-network"
        variables={{
            userId: R.or(
                R.path(["researchRequest", "requester", "id"], props),
                R.path(["auth", "user_id"], props)
            ),
        }}
    >
        {withApolloState(apolloProps => (
            <WrappedResearchRequestForm {...props} {...apolloProps.data} />
        ))}
    </Query>
);

export default WithAuth(ResearchRequestFormQuery);
